import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { CarouselUserAction } from "@app-cfs/shared/models";

export interface AppStateInfo {
  isSuspended: boolean;
  isInteractionAllowed: boolean;
  isActionModalOpened: boolean;
  isCarouselPlayAllowed: boolean;
}

@Injectable({
  providedIn: "root"
})
export class StateService {
  private _isActionModalOpened = false;
  private isSuspendedSrc$ = new BehaviorSubject<boolean>(false);
  private userActionSrc$ = new Subject<CarouselUserAction>();

  readonly isSuspended$ = this.isSuspendedSrc$.asObservable();
  readonly userAction$ = this.userActionSrc$.asObservable();

  get isActionModalOpened(): boolean {
    return this._isActionModalOpened;
  }

  get isSuspended(): boolean {
    return this.isSuspendedSrc$.value;
  }

  get isInteractionAllowed(): boolean {
    return !this.isSuspended;
  }

  /**
   * CFS Carousel should not be played if:
   * 1. App is suspended;
   * 2. Forced slide is displaying over carousel (inner carousel service logic);
   * 3. Action modal is opened;
   */
  get isCarouselPlayAllowed(): boolean {
    return !this.isSuspended && !this.isActionModalOpened;
  }

  getStateInfo(): AppStateInfo {
    return {
      isSuspended: this.isSuspended,
      isInteractionAllowed: this.isInteractionAllowed,
      isActionModalOpened: this.isActionModalOpened,
      isCarouselPlayAllowed: this.isCarouselPlayAllowed
    };
  }

  setActionModalOpened(): void {
    this._isActionModalOpened = true;
  }

  setActionModalClosed(hasAfterCloseAction = false): void {
    this._isActionModalOpened = !!hasAfterCloseAction;
  }

  setSuspend(): void {
    this.isSuspendedSrc$.next(true);
  }

  setResume(): void {
    this.isSuspendedSrc$.next(false);
  }

  sendUserAction(action: CarouselUserAction): void {
    if (this.isInteractionAllowed) {
      this.userActionSrc$.next(action);
    }
  }
}
